import React, { useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, Inbox, FileSpreadsheet, FileDown } from 'lucide-react';
import EmptyState from './EmptyState';
import { Skeleton, TableSkeleton } from './Skeleton';
import { exportColumns } from '../../utils/exportExcel';
import { downloadTableAsPdf } from '../../utils/exportPdf';

export default function DataTable({ columns, data, searchFields = [], actions, onRowClick, pageSize = 15, emptyState, loading, exportFilename, totals }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const filtered = useMemo(() => {
    let result = data;
    if (search && searchFields.length) {
      const term = search.toLowerCase();
      result = result.filter(row =>
        searchFields.some(f => {
          const val = typeof f === 'function' ? f(row) : row[f];
          return val && String(val).toLowerCase().includes(term);
        })
      );
    }
    if (sortKey) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [data, search, searchFields, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (loading) {
    return (
      <div>
        {searchFields.length > 0 && <div className="mb-4 relative"><Skeleton className="h-10 max-w-sm rounded-lg" /></div>}
        <TableSkeleton rows={5} cols={columns.length} />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="card">
        {searchFields.length > 0 && (
          <div className="mb-4 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="input pl-9 max-w-sm"
              data-datatable-search
            />
          </div>
        )}
        <EmptyState
          icon={emptyState?.icon || Inbox}
          title={emptyState?.title || 'No records found'}
          description={emptyState?.description}
          actionLabel={emptyState?.actionLabel}
          onAction={emptyState?.onAction}
        />
      </div>
    );
  }

  return (
    <div>
      {(searchFields.length > 0 || exportFilename) && (
        <div className="mb-4 flex items-center justify-between gap-3">
          {searchFields.length > 0 ? (
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search... (Press / to focus)"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="input pl-9 w-full"
                data-datatable-search
              />
            </div>
          ) : <div />}
          <div className="flex items-center gap-2">
            {exportFilename && (
              <button
                type="button"
                onClick={() => exportColumns(exportFilename, columns, filtered)}
                disabled={filtered.length === 0}
                className="btn btn-secondary btn-sm flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                title="Export the current view to a CSV file (opens in Excel)"
              >
                <FileSpreadsheet size={15} /> Export to Excel
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                if (pdfBusy || filtered.length === 0) return;
                setPdfBusy(true);
                try {
                  const pageTitle = (typeof document !== 'undefined' && document.querySelector('.page-title')?.textContent?.trim()) || '';
                  const title = pageTitle || (exportFilename ? exportFilename.replace(/[-_]/g, ' ') : 'List');
                  await downloadTableAsPdf(title, columns, filtered);
                } catch (e) { console.error('Table PDF failed:', e); }
                finally { setPdfBusy(false); }
              }}
              disabled={filtered.length === 0 || pdfBusy}
              className="btn btn-secondary btn-sm flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
              title="Download the current view as a PDF"
            >
              <FileDown size={15} /> {pdfBusy ? 'Generating…' : 'Download PDF'}
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`${col.sortable ? 'cursor-pointer select-none' : ''} ${col.align === 'right' ? '!text-right' : ''}`}
                  onClick={() => col.sortable && handleSort(col.key)}
                  style={col.width ? { width: col.width } : undefined}
                >
                  <span className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                    {col.label}
                    {sortKey === col.key && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                  </span>
                </th>
              ))}
              {actions && <th className="sticky-header" style={{ width: '120px' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-12 text-gray-400">
                  No results match your search
                </td>
              </tr>
            ) : (
              paged.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  onClick={() => onRowClick?.(row)}
                  className={onRowClick ? 'cursor-pointer' : ''}
                >
                  {columns.map(col => (
                    <td key={col.key} className={col.align === 'right' ? 'text-right' : ''}>
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                  {actions && (
                    <td>
                      <div className="flex items-center gap-1">
                        {actions(row)}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
          {totals && filtered.length > 0 && (() => {
            const totalsData = totals(filtered) || {};
            let labelPlaced = false;
            return (
              <tfoot>
                <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                  {columns.map(col => {
                    const cell = totalsData[col.key];
                    let content = cell;
                    if (cell == null && !labelPlaced) { content = 'Total'; labelPlaced = true; }
                    return (
                      <td key={col.key} className={col.align === 'right' ? 'text-right' : ''}>
                        {content}
                      </td>
                    );
                  })}
                  {actions && <td />}
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
          <span>Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, filtered.length)} of {filtered.length}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-sm btn-secondary"
            >
              <ChevronLeft size={14} />
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn btn-sm btn-secondary"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
